import type { TripStatus, VehicleType } from '@raahi/shared';
import { request } from './client.js';

export interface Overview {
  activeTrips: number;
  onlineDrivers: number;
  todayTrips: number;
  todayRevenue: number;
  tripsByHour: Array<{ hour: number; count: number }>;
}

export interface LiveDriver {
  id: string;
  name: string;
  status: string;
  position: { lat: number; lng: number };
}

export interface LiveTrip {
  id: string;
  status: TripStatus;
  rider: string;
  driver: string | null;
  pickup: { lat: number; lng: number; label: string };
  drop: { lat: number; lng: number; label: string };
  fareTotal: number;
}

export interface LiveSnapshot {
  drivers: LiveDriver[];
  trips: LiveTrip[];
}

export interface DriverRow {
  id: string;
  userId: string;
  status: string;
  approved: boolean;
  rating: number;
  lat: number | null;
  lng: number | null;
  user: { id: string; name: string; email: string; phone: string };
  vehicle: { make: string; model: string; plate: string; color: string; type: VehicleType } | null;
}

export interface RiderRow {
  id: string;
  name: string;
  email: string;
  phone: string;
  createdAt: string;
  completedTrips: number;
  totalSpend: number;
}

export interface TripRow {
  id: string;
  status: TripStatus;
  vehicleType: VehicleType;
  pickupLabel: string;
  dropLabel: string;
  fareTotal: number;
  paymentMethod: string;
  requestedAt: string;
  rider: { name: string };
  driver: { user: { name: string } } | null;
}

export interface FareConfigRow {
  id: string;
  vehicleType: VehicleType;
  base: number;
  perKm: number;
  perMin: number;
  minFare: number;
}

export interface SurgeRow {
  id: string;
  name: string;
  multiplier: number;
  active: boolean;
}

export const overview = () => request<Overview>('/admin/overview');
export const liveSnapshot = () => request<LiveSnapshot>('/admin/live');
export const listDrivers = () => request<DriverRow[]>('/admin/drivers');
export const approveDriver = (id: string, approved: boolean) =>
  request<{ id: string; approved: boolean; status: string }>(`/admin/drivers/${id}`, {
    method: 'PATCH',
    body: { approved },
  });
export const listRiders = () => request<RiderRow[]>('/admin/riders');
export const listTrips = (filter: { status?: string }) =>
  request<TripRow[]>('/admin/trips', { query: filter });
export const getPricing = () => request<{ fares: FareConfigRow[]; surge: SurgeRow[] }>('/admin/pricing');
export const updateFare = (input: FareConfigRow) =>
  request<FareConfigRow>('/admin/pricing/fare', { method: 'PUT', body: input });
export const upsertSurge = (input: { id?: string; name: string; multiplier: number; active: boolean }) =>
  request<SurgeRow>('/admin/pricing/surge', { method: 'PUT', body: input });
export const listPayouts = () =>
  request<Array<{ id: string; amount: number; periodStart: string; periodEnd: string; status: string; driver: { user: { name: string } } }>>('/admin/payouts');
export const markPayoutPaid = (id: string) =>
  request<{ id: string; status: string }>(`/admin/payouts/${id}/paid`, { method: 'POST' });
